/**
 * Tier-Feature Registry — single source of truth for all gateable features
 * and tier assignments.
 *
 * Previously split across three locations:
 *   - services/control-plane/src/billing/feature-catalog.ts (catalog + helpers)
 *   - hank/src/api/routes/config.ts (TIER_FEATURES local copy)
 *   - conceptually via connectCatalog.ts UI (follow-up)
 *
 * All new reads should import from here. The CP feature-catalog.ts file is
 * now a thin re-export shim so existing CP-internal imports remain path-stable.
 *
 * Tracker: FV-06 (UI-vs-runtime, tactical). Mirrors PR #312's pattern.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type Tier = "starter" | "pro" | "enterprise";

export interface FeatureDef {
  /** Unique feature key stored in config_json.features */
  key: string;
  /** Human-readable label for UI / error messages */
  label: string;
  /** Minimum tier that includes this feature */
  tier: Tier;
  /** URL path prefixes matched at the gateway middleware layer */
  gatewayPatterns?: string[];
  /** Hank API/page route prefixes matched at the Hank API layer */
  hankPatterns?: string[];
  /** Agent tool names that require this feature */
  toolNames?: string[];
  /** Whether this feature can be purchased as an add-on for lower tiers */
  purchasable: boolean;
  /** Monthly add-on price in USD (if purchasable) */
  priceUsd?: number;
  /**
   * Manifest id of the plugin that backs this feature (e.g. "process-optimizer").
   * When set, the feature gate consults per-tenant overrides at
   * `config.plugins.overrides[pluginId]` before falling through to tier logic.
   *
   * Admin/ops features (plugins, systemHealth, middlewareConfig, etc.) leave
   * this unset because they are surfaces, not plugin-backed capabilities.
   *
   * Drift guard: every non-null value must correspond to a real manifest id in
   * `extensions/*​/agentme.plugin.json`. Enforced in
   * `tier-feature-registry.test.ts` so catalog-vs-filesystem divergence fails
   * CI rather than showing up as a "plugin override had no effect" bug report.
   */
  pluginId?: string;
}

// ─── Tier Rank Helper ────────────────────────────────────────────────────────

export const TIER_RANK: Record<string, number> = {
  starter: 0,
  pro: 1,
  enterprise: 2,
};

// Temporary cert posture: keep admin/ops surfaces enterprise-only while the
// rest of the end-user product remains broadly available across subscriptions.
export const ADMIN_ONLY_FEATURES = new Set([
  "plugins",
  "optimizer",
  "devLoop",
  "securityEvents",
  "systemHealth",
  "middlewareConfig",
  "configEditor",
  "injectionDefense",
]);

export function getTierRank(tier: string): number {
  return TIER_RANK[tier] ?? 0;
}

// ─── Catalog ─────────────────────────────────────────────────────────────────

export const FEATURE_CATALOG: FeatureDef[] = [
  // ── Pro features ─────────────────────────────────────────────────────────
  {
    key: "models",
    label: "Model Management",
    tier: "pro",
    hankPatterns: ["/api/models"],
    purchasable: true,
    priceUsd: 10,
  },
  {
    key: "rateLimits",
    label: "Rate Limit Controls",
    tier: "pro",
    hankPatterns: ["/api/rate-limits"],
    purchasable: false,
  },
  {
    key: "browserAccess",
    label: "Browser Access Config",
    tier: "pro",
    hankPatterns: ["/api/browser-access"],
    purchasable: false,
  },
  {
    key: "workspaceAccess",
    label: "Workspace Access Control",
    tier: "pro",
    hankPatterns: ["/api/workspace-access"],
    purchasable: false,
  },
  {
    key: "mandates",
    label: "Task Mandates",
    tier: "pro",
    hankPatterns: ["/api/mandates"],
    purchasable: true,
    priceUsd: 5,
  },
  {
    key: "contextFiles",
    label: "Context Files",
    tier: "starter",
    hankPatterns: ["/api/context-files"],
    purchasable: true,
    priceUsd: 5,
  },
  {
    key: "emailDrafts",
    label: "Email Drafts",
    tier: "pro",
    hankPatterns: ["/api/drafts"],
    purchasable: true,
    priceUsd: 5,
  },
  {
    key: "dataSources",
    label: "Data Sources",
    tier: "pro",
    hankPatterns: ["/api/data-sources"],
    purchasable: true,
    priceUsd: 10,
  },
  {
    key: "agentEmail",
    label: "Agent Email",
    tier: "pro",
    hankPatterns: ["/api/agent-email"],
    purchasable: true,
    priceUsd: 10,
  },
  {
    key: "integrations",
    label: "Integrations",
    tier: "pro",
    hankPatterns: ["/api/integrations"],
    purchasable: true,
    priceUsd: 10,
  },
  {
    key: "plugins",
    label: "Plugin Management",
    tier: "enterprise",
    hankPatterns: ["/api/plugins"],
    purchasable: false,
  },
  {
    key: "services",
    label: "Service Management",
    tier: "pro",
    hankPatterns: ["/api/services"],
    purchasable: false,
  },
  {
    key: "usageAnalytics",
    label: "Usage Analytics",
    tier: "pro",
    hankPatterns: ["/api/usage"],
    purchasable: true,
    priceUsd: 5,
  },
  {
    key: "challengeQueue",
    label: "Challenge Resolution",
    tier: "pro",
    hankPatterns: ["/api/challenges"],
    purchasable: false,
  },
  {
    key: "optimizer",
    label: "Process Optimizer",
    tier: "enterprise",
    gatewayPatterns: ["/optimizer"],
    hankPatterns: ["/api/optimizer"],
    toolNames: ["optimizer_status"],
    purchasable: false,
    pluginId: "process-optimizer",
  },
  {
    key: "devLoop",
    label: "Dev Loop",
    tier: "enterprise",
    hankPatterns: ["/api/dev-loop"],
    purchasable: false,
    pluginId: "dev-loop",
  },
  {
    key: "securityEvents",
    label: "Security Events",
    tier: "enterprise",
    hankPatterns: ["/api/security-events"],
    purchasable: false,
  },
  {
    key: "systemHealth",
    label: "System Health Detail",
    tier: "enterprise",
    hankPatterns: ["/api/system-health"],
    purchasable: false,
  },
  // W5-I.a: memory is a core runtime subsystem, not a gated feature.
  // memory_search / memory_get / memory_write / memory_archive tools
  // are always available when memorySearch.enabled !== false. Entry
  // retained for historical record only; the gateway pattern is not
  // enforced.
  {
    key: "memoryPrimer",
    label: "Memory (core subsystem)",
    tier: "starter",
    toolNames: ["memory_search", "memory_get", "memory_write", "memory_archive"],
    purchasable: false,
  },
  // travelDomain is an app-shaped feature owned by the agent-bootstrap plugin:
  // its domain logic lives in extensions/agent-bootstrap/src/domains/travel.ts
  // and the 7 travel tools are registered via agent-bootstrap. Mapping to the
  // agent-bootstrap pluginId lets tenants disable travel via a plugin override
  // (config.plugins.overrides["agent-bootstrap"] = false) alongside the rest
  // of the bootstrap surface. Per user framing: "travel Domain is more like
  // an app" — agent-bootstrap owns the surface.
  {
    key: "travelDomain",
    label: "Travel Management",
    tier: "pro",
    // NOTE: hankPatterns are informational — Hank's feature-gate intentionally
    // does NOT enforce a 403 prefix match on travel routes. Actual gating uses
    // toolNames (checked in the gateway middleware). Listed here only so Adam
    // surfaces and the Hank admin UI know travel is the intended owner of
    // those URL prefixes. See buildHankFeaturePatterns() for how this entry is
    // excluded from the derived pattern list.
    hankPatterns: ["/api/travel", "/api/bootstrap/travel", "/api/agent-calendar"],
    toolNames: [
      "travel_query",
      "travel_upsert",
      "travel_search",
      "request_booking",
      "check_wallet",
      "write_agent_calendar",
      "list_agent_calendars",
    ],
    purchasable: false,
    pluginId: "agent-bootstrap",
  },

  // ── Enterprise features ──────────────────────────────────────────────────
  {
    key: "middlewareConfig",
    label: "Middleware Configuration",
    tier: "enterprise",
    hankPatterns: ["/api/middleware-config"],
    purchasable: false,
  },
  {
    key: "configEditor",
    label: "Config Editor",
    tier: "enterprise",
    hankPatterns: ["/api/config-files"],
    purchasable: false,
  },
  {
    key: "injectionDefense",
    label: "Injection Defense Tuning",
    tier: "enterprise",
    hankPatterns: ["/api/injection-defense"],
    purchasable: false,
  },
  {
    key: "operatorModel",
    label: "Operator Model",
    tier: "enterprise",
    gatewayPatterns: ["/operator-model"],
    hankPatterns: ["/api/operator-model"],
    purchasable: true,
    priceUsd: 15,
    pluginId: "operator-model",
  },
  // Surface-only: relationshipGraph is a shared core DB subsystem owned by
  // services/data-access/src/core/. No dedicated plugin manifest today. Map
  // to a pluginId if the graph later becomes plugin-hosted.
  {
    key: "relationshipGraph",
    label: "Relationship Graph",
    tier: "enterprise",
    hankPatterns: ["/api/graph"],
    purchasable: true,
    priceUsd: 10,
  },
  {
    key: "workspaceZones",
    label: "Workspace Zones",
    tier: "enterprise",
    gatewayPatterns: ["/workspace-zones"],
    hankPatterns: ["/api/workspace-zones"],
    purchasable: false,
  },
];

// ─── Derived Helpers ─────────────────────────────────────────────────────────

/**
 * Build a features map for a given tier.
 * Returns Record<string, boolean> where each catalog key is true if the
 * tier's rank is >= the feature's minimum tier rank.
 */
export function getDefaultFeatures(tier: string): Record<string, boolean> {
  const rank = Object.prototype.hasOwnProperty.call(TIER_RANK, tier) ? getTierRank(tier) : -1;
  const features: Record<string, boolean> = {};
  for (const f of FEATURE_CATALOG) {
    const tierEntitled = rank >= 0 && getTierRank(f.tier) <= rank;
    const broadlyEnabled = rank >= 0 && !ADMIN_ONLY_FEATURES.has(f.key);
    features[f.key] = tierEntitled || broadlyEnabled;
  }
  return features;
}

/**
 * Get all purchasable features (for Stripe add-on catalog display).
 */
export function getPurchasableFeatures(): FeatureDef[] {
  return FEATURE_CATALOG.filter((f) => f.purchasable);
}

/**
 * Look up a feature by key.
 */
export function getFeature(key: string): FeatureDef | undefined {
  return FEATURE_CATALOG.find((f) => f.key === key);
}

/**
 * Return the set of plugin ids that back at least one catalog feature.
 *
 * This is the authoritative set of plugins a tenant can toggle via
 * `config.plugins.overrides[pluginId]`. Used by the feature gate to decide
 * whether a feature is plugin-backed, and by the Adam override UI to list
 * eligible plugins.
 */
export function getPluginBackedIds(): Set<string> {
  const ids = new Set<string>();
  for (const f of FEATURE_CATALOG) {
    if (f.pluginId) ids.add(f.pluginId);
  }
  return ids;
}

/**
 * Return the catalog features backed by a given plugin id, or `[]` if none.
 * Used by block responses to explain which features a plugin override affects.
 */
export function getFeaturesForPlugin(pluginId: string): FeatureDef[] {
  return FEATURE_CATALOG.filter((f) => f.pluginId === pluginId);
}

/**
 * Build the per-tier feature key lists.
 *
 * Returns the feature keys assigned to each tier — i.e. the features whose
 * minimum tier equals that tier exactly. This replaces the hardcoded
 * TIER_FEATURES constant that previously lived in hank/src/api/routes/config.ts
 * and was the canonical drift point flagged by FV-06.
 *
 * The output matches the previous Hank TIER_FEATURES exactly:
 *   starter: ["contextFiles"]
 *   pro:     12 keys (models, rateLimits, browserAccess, workspaceAccess,
 *              mandates, emailDrafts, dataSources, agentEmail, integrations,
 *              services, usageAnalytics, challengeQueue)
 *   enterprise: 11 keys (middlewareConfig, configEditor, injectionDefense,
 *              operatorModel, relationshipGraph, workspaceZones, plugins,
 *              optimizer, devLoop, securityEvents, systemHealth)
 *
 * Note: memoryPrimer and travelDomain are intentionally excluded because the
 * original Hank TIER_FEATURES did not include them (travelDomain is gated via
 * toolNames, memoryPrimer is a core subsystem marker). This preserves the
 * resolveTier() behavior in config.ts exactly.
 */
export function buildTierFeatureKeys(): Record<Tier, string[]> {
  const EXCLUDE_FROM_TIER_FEATURES = new Set(["memoryPrimer", "travelDomain"]);

  const result: Record<Tier, string[]> = {
    starter: [],
    pro: [],
    enterprise: [],
  };

  for (const f of FEATURE_CATALOG) {
    if (EXCLUDE_FROM_TIER_FEATURES.has(f.key)) continue;
    result[f.tier].push(f.key);
  }

  return result;
}

/**
 * Features whose `hankPatterns` are informational-only — listed in the
 * catalog to document URL-prefix ownership but NOT enforced by Hank's
 * feature-gate middleware. Today only travelDomain: it's gated via
 * toolNames at the gateway layer, not via Hank URL prefix.
 *
 * Exported so downstream tooling (e.g. Adam route inventory) can reason
 * about which features Hank actually 403s on.
 */
export const HANK_INFORMATIONAL_PATTERN_KEYS: readonly string[] = ["travelDomain"];

export interface HankFeaturePattern {
  key: string;
  label: string;
  tier: string;
  patterns: string[];
}

/**
 * Build the list of features that Hank's feature-gate middleware enforces
 * as URL-prefix 403s. Previously a hand-maintained copy in
 * `hank/src/api/middleware/feature-gate.ts` — bucket A consolidates it as
 * a derivation from FEATURE_CATALOG so feature-set/label/tier changes made
 * here propagate automatically.
 *
 * Excludes features listed in HANK_INFORMATIONAL_PATTERN_KEYS (currently
 * only travelDomain). Preserves the exact set Hank was enforcing before
 * the consolidation — see tier-feature-registry.test.ts for the
 * two-sided drift guard.
 */
export function buildHankFeaturePatterns(): HankFeaturePattern[] {
  const informational = new Set(HANK_INFORMATIONAL_PATTERN_KEYS);
  const result: HankFeaturePattern[] = [];
  for (const f of FEATURE_CATALOG) {
    if (!f.hankPatterns || f.hankPatterns.length === 0) continue;
    if (informational.has(f.key)) continue;
    result.push({
      key: f.key,
      label: f.label,
      tier: f.tier,
      patterns: [...f.hankPatterns],
    });
  }
  return result;
}

// ─── Gateway feature catalog (P3 Track O Bundle 5) ───────────────────────────

/**
 * Subset of FeatureDef that the gateway middleware (`src/middleware/feature-gate.ts`)
 * needs for its pattern + tool index. Exposes only the fields the gate reads —
 * the full catalog has UI-only fields (priceUsd, purchasable) that the runtime
 * gate doesn't consume.
 */
export interface GatewayFeatureDef {
  key: string;
  label: string;
  tier: Tier;
  gatewayPatterns?: string[];
  hankPatterns?: string[];
  toolNames?: string[];
  pluginId?: string;
}

/**
 * Build the catalog the gateway feature-gate middleware consumes.
 *
 * Previously a hand-maintained inline copy in `src/middleware/feature-gate.ts`
 * (`GATEWAY_FEATURE_CATALOG`) that drifted from this registry — most visibly
 * `optimizer.tier: "pro"` in the gateway vs. `"enterprise"` here. β audit
 * (PR #463) flagged the drift; Phase 3 Track O Bundle 5 closes it by
 * deriving the gateway catalog from the same source of truth.
 *
 * Filters to entries that the gateway can actually match against — i.e. an
 * entry must have at least one of `gatewayPatterns`, `hankPatterns`, or
 * `toolNames`. Entries with only informational fields (e.g. `memoryPrimer`,
 * which the registry comment marks as "core subsystem, gateway pattern not
 * enforced") are excluded so they don't acquire a phantom enforcement path
 * via the derivation.
 *
 * See `tier-feature-registry.test.ts` for the drift-guard test.
 */
export function buildGatewayFeaturePatterns(): GatewayFeatureDef[] {
  const informational = new Set(HANK_INFORMATIONAL_PATTERN_KEYS);
  const result: GatewayFeatureDef[] = [];
  for (const f of FEATURE_CATALOG) {
    if (informational.has(f.key)) continue;
    const hasGatewayPattern = !!f.gatewayPatterns && f.gatewayPatterns.length > 0;
    const hasHankPattern = !!f.hankPatterns && f.hankPatterns.length > 0;
    const hasToolName = !!f.toolNames && f.toolNames.length > 0;
    if (!hasGatewayPattern && !hasHankPattern && !hasToolName) continue;
    // Exclude memoryPrimer: per the registry comment its tools are "always
    // available when memorySearch.enabled !== false. Entry retained for
    // historical record only; the gateway pattern is not enforced." Including
    // it here would flip that intent into runtime gating, which is not what
    // this consolidation is supposed to do.
    if (f.key === "memoryPrimer") continue;
    const entry: GatewayFeatureDef = {
      key: f.key,
      label: f.label,
      tier: f.tier,
    };
    if (hasGatewayPattern) entry.gatewayPatterns = [...f.gatewayPatterns!];
    if (hasHankPattern) entry.hankPatterns = [...f.hankPatterns!];
    if (hasToolName) entry.toolNames = [...f.toolNames!];
    if (f.pluginId) entry.pluginId = f.pluginId;
    result.push(entry);
  }
  return result;
}
